import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Typography,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { PATH_DASHBOARD } from "@app/routes/paths";
import { useRequestState } from "@app/hooks";
import { selectMyProfileState } from "@app/modules/my-profile/selector";
import { myProfile } from "@app/modules/my-profile/slice";

interface UserMenuProps {
  userName: string;
  onEditProfile: () => void;
  onMyOrders: () => void;
  onLogout: () => void;
}

const AdminProfilePopup: React.FC<UserMenuProps> = ({
  userName,
  onEditProfile,
  onMyOrders,
  onLogout,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data } = useRequestState({
    stateSelector: selectMyProfileState,
  });
  useEffect(() => {
    dispatch(myProfile());
  }, []);

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          "&:hover": {
            backgroundColor: "transparent",
          },
          p: 0,
        }}
      >
        <Avatar
          sx={{
            width: isMobile ? 28 : 35,
            height: isMobile ? 28 : 35,
            bgcolor:  theme.palette.primary.contrastText,
            color: theme.palette.primary.main,
            border: 1,
            borderColor: theme.palette.primary.main,
            position: "absolute",
            top: "85%",
            transform: "translateY(-50%)",
          }}
          src={data?.profileImage || undefined}
        >
          {!data?.profileImage &&
            data?.firstName &&
            `${data.firstName[0] || ""}${
              data.lastName?.[0] || ""
            }`.toUpperCase()}
        </Avatar>
      </IconButton>

      {open && (
        <Box
          component="img"
          src="/assets/images/user-profile-arrow.png"
          alt="Arrow"
          sx={{
            position: "absolute",
            top: 49,
            right: 90,
            width: 20,
            height: 20,
            zIndex: 1300,
            display: { xs: "none", sm: "block" },
          }}
        />
      )}

      <Stack>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{
            mt: 2,
            ml: { xs: 0, sm: 5 },
          }}
          PaperProps={{
            sx: {
              mt: 2, 
              p: 0,
              width: { xs: "90vw", sm: 250 },
              borderRadius: 1,
              overflow: "hidden",
              position: "relative",
            },
          }}
        >
          <Box
            sx={{
              bgcolor: theme.palette.primary.light,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: 2,
              pb: 1,
              height: 56,
              bottom: 10,
              position: "relative",
            }}
          >
            <Avatar
              sx={{
                width: isMobile ? 28 : 55,
                height: isMobile ? 28 : 55,
                bgcolor:  theme.palette.primary.contrastText,
                color: theme.palette.primary.main,
                border: 1,
                borderColor: theme.palette.primary.main,
                position: "absolute",
                top: "85%",
                transform: "translateY(-50%)",
              }}
              src={data?.profileImage || undefined}
            >
              {!data?.profileImage &&
                data?.firstName &&
                `${data.firstName[0] || ""}${
                  data.lastName?.[0] || ""
                }`.toUpperCase()}
            </Avatar>
            <Box sx={{ height: 40, mt: 8 }} />
            <Typography
              fontFamily={"Poppins"}
              sx={{
                mt: 1,
                color: "black",
                fontWeight: 300,
                fontSize: { xs: "1rem", sm: "1.1rem" },
              }}
            >
              {userName}
            </Typography>
          </Box>

          <Box sx={{ px: 0, py: 2, mt: 6 }}>
            <MenuItem
              onClick={() => {
                setSelectedItem("edit");
                handleClose();
                onEditProfile();
                navigate(PATH_DASHBOARD.myProfile);
              }}
              sx={{
                color: selectedItem === "edit" ? theme.palette.primary.main : "inherit",
                fontFamily: "Poppins",
                fontWeight: 300,
              }}
            >
              <Icon
                icon="tabler:edit"
                width={20}
                style={{ marginRight: 8 }}
                color={selectedItem === "edit" ? "#50A5FF" : undefined}
              />
              Edit Profile
            </MenuItem>
            <Divider sx={{ my: 1.5 }} />
          </Box>
        </Menu>
      </Stack>
    </>
  );
};

export { AdminProfilePopup };
